import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { useAuthContext } from 'src/context/auth.context';
import {
  UserFilterInput,
  UsersDocument,
  UsersQuery,
} from 'src/graphql/generated/graphql';

interface UseUsersProps {
  filter?: UserFilterInput;
  pagination?: {
    limit: number;
    page: number;
    extraRows?: number;
  };
  moveAuthenticatedUserFirst?: boolean;
  onCompleted?: (data: UsersQuery) => void;
  enabled?: boolean;
}

export const useUsers = ({
  filter,
  pagination,
  moveAuthenticatedUserFirst = false,
  onCompleted,
  enabled = true,
}: UseUsersProps = {}) => {
  const { user: authenticatedUser } = useAuthContext();

  const { limit = 100, page = 0, extraRows = 0 } = pagination || {};
  const offset = page * limit;

  const { data, loading } = useQuery(UsersDocument, {
    variables: {
      filter,
      pagination: {
        limit: limit + extraRows,
        offset,
      },
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-only',
    onCompleted: onCompleted,
    skip: !enabled,
  });

  const records = useMemo(
    () => [...(data?.users?.records || [])],
    [data?.users?.records],
  );

  if (moveAuthenticatedUserFirst && authenticatedUser) {
    const meIndex = records.findIndex(
      (user) => user.id === authenticatedUser.id,
    );

    if (meIndex !== -1) {
      const me = records.splice(meIndex, 1);
      records.unshift(me[0]);
    }
  }

  return {
    users: records,
    totalCount: data?.users?.pagination.totalCount || 0,
    loading,
  };
};
